export const EMPTY_CELL = '—';

export type RecordRoomFileStatus = 'ptr' | 'stor' | 'pret' | 'retr';

/** Null/empty API values display as dash in the table. */
export function displayOrDash(value: string | null | undefined): string {
  const t = typeof value === 'string' ? value.trim() : '';
  return t || EMPTY_CELL;
}

/** Show Update Storage Location when a stored location exists. */
export function hasStorageLocation(storageLocation: string): boolean {
  const t = storageLocation.trim();
  return t.length > 0 && t !== EMPTY_CELL;
}

export type CompletedFileRow = {
  ref: string;
  fileCategory: string;
  subFileCategory: string;
  agency: string;
  completedAt: string;
  statusLabel: string;
  statusCode: string;
  statusClass: string;
  movementAt: string;
  custody: string;
  storageLocation: string;
  caseRequestId: string;
  /** @deprecated Mock-only status key. */
  fs?: RecordRoomFileStatus;
};

export const rrFsLabel: Record<RecordRoomFileStatus, string> = {
  ptr: 'Pending Transfer to Record Room',
  stor: 'Stored',
  pret: 'Pending Retrieval',
  retr: 'Retrieved',
};

export const rrFsClass: Record<RecordRoomFileStatus, string> = {
  ptr: 'p-ptra',
  stor: 'p-stor',
  pret: 'p-pretv',
  retr: 'p-retrv',
};

function mockRow(
  ref: string,
  fs: RecordRoomFileStatus,
  partial: Omit<CompletedFileRow, 'ref' | 'statusLabel' | 'statusCode' | 'statusClass' | 'fs'>,
): CompletedFileRow {
  return {
    ref,
    fs,
    statusLabel: rrFsLabel[fs],
    statusCode: fs,
    statusClass: rrFsClass[fs],
    ...partial,
  };
}

export const completedFiles: CompletedFileRow[] = [
  mockRow('E/104/2026/AGD', 'ptr', {
    fileCategory: 'Civil Division - Advise',
    subFileCategory: 'E - General Legal Advice',
    agency: 'Department of Land Settlement',
    completedAt: '22 Mar 2026, 10:00 am',
    movementAt: '22 Mar 2026, 10:05 am',
    custody: 'Ms. A. Perera',
    storageLocation: EMPTY_CELL,
    caseRequestId: 'mock-e-104-2026',
  }),
  mockRow('WP1/112/2026', 'stor', {
    fileCategory: 'Criminal',
    subFileCategory: 'CH - Child Abuse',
    agency: 'NCPA',
    completedAt: '22 Mar 2026, 10:00 am',
    movementAt: '22 Mar 2026, 10:05 am',
    custody: 'Record Room',
    storageLocation: 'Rack A-12',
    caseRequestId: 'mock-wp1-112-2026',
  }),
  mockRow('E/200/2026/AGD', 'pret', {
    fileCategory: 'Civil Appellate Division',
    subFileCategory: 'COA - Court of Appeal',
    agency: 'Sri Lanka Police',
    completedAt: '22 Mar 2026, 10:00 am',
    movementAt: '22 Mar 2026, 10:05 am',
    custody: 'Record Room',
    storageLocation: 'Rack B-05',
    caseRequestId: 'mock-e-200-2026',
  }),
  mockRow('PP/05/2026/AGD', 'retr', {
    fileCategory: 'Public Petitions',
    subFileCategory: '—',
    agency: 'Ministry of Health',
    completedAt: '22 Mar 2026, 10:00 am',
    movementAt: '22 Mar 2026, 10:05 am',
    custody: 'Mr. D. Silva',
    storageLocation: EMPTY_CELL,
    caseRequestId: 'mock-pp-05-2026',
  }),
  mockRow('RTI/12/2026/AGD', 'ptr', {
    fileCategory: 'Right to Information',
    subFileCategory: '—',
    agency: 'Ministry of Education',
    completedAt: '22 Mar 2026, 10:00 am',
    movementAt: '22 Mar 2026, 10:05 am',
    custody: EMPTY_CELL,
    storageLocation: EMPTY_CELL,
    caseRequestId: 'mock-rti-12-2026',
  }),
];

/** Pill CSS class from API status code or display name. */
export function recordRoomStatusPillClass(statusCode: string, statusName: string): string {
  const code = statusCode.toLowerCase();
  const name = statusName.toLowerCase();
  if (
    code.includes('ptr') ||
    code.includes('pending_transfer') ||
    (name.includes('pending') && name.includes('transfer'))
  ) {
    return rrFsClass.ptr;
  }
  if (code.includes('stor') || (name.includes('stored') && !name.includes('pending'))) return rrFsClass.stor;
  if (code.includes('pret') || (name.includes('pending') && name.includes('retrieval'))) return rrFsClass.pret;
  if (code.includes('retr') || name.includes('retrieved')) return rrFsClass.retr;
  return rrFsClass.ptr;
}
